<template>
  <div class="enhanced-notifications">
    <!-- Enhanced Authentication Notifications -->
    <div v-if="authNotifications.length > 0" class="auth-notifications">
      <login-warning
        v-for="notification in authNotifications"
        :key="notification.id"
        :error="notification.error"
        :visible="true"
        @unlock="handleUnlock(notification)"
        @close="removeNotification(notification)"
      />
    </div>

    <!-- Regular Notifications (fallback to UI library) -->
    <div v-if="regularNotifications.length > 0" class="regular-notifications">
      <div
        v-for="notification in regularNotifications"
        :key="notification.id"
        class="notification"
        :class="getNotificationClass(notification)"
      >
        <button
          class="delete"
          @click="removeNotification(notification)"
        ></button>

        <div class="notification-content">
          <div v-if="notification.error" class="notification-error">
            <strong>{{ notification.error.message || notification.error }}</strong>
          </div>
          <div v-else-if="notification.text" class="notification-text">
            {{ notification.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoginWarning from './LoginWarning.vue'

/**
 * Enhanced Notifications Component
 *
 * Handles both enhanced authentication notifications with LoginWarning
 * and regular notifications from the UI library.
 *
 * Note: Suppresses success notifications for authentication/login operations
 * while preserving error and warning notifications for all cases.
 */
export default {
  name: 'EnhancedNotifications',
  components: {
    LoginWarning
  },
  props: {
    name: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      notifications: []
    }
  },
  computed: {
    /**
     * Filter notifications for enhanced authentication errors
     * Note: Success notifications are already filtered out in addNotification/loadNotifications
     */
    authNotifications() {
      return this.notifications.filter(notification =>
        notification.from === 'authentication' &&
        notification.error &&
        (notification.error.enhanced || notification.error.loginAttempts || notification.error.accountLocked)
      )
    },

    /**
     * Filter notifications for regular display
     * Note: Authentication success notifications are suppressed at the source
     */
    regularNotifications() {
      return this.notifications.filter(notification =>
        notification.from === 'authentication' &&
        notification.error &&
        !notification.error.enhanced &&
        !notification.error.loginAttempts &&
        !notification.error.accountLocked
      )
    }
  },
  created() {
    // Subscribe to store notifications
    this.$store.subscribe((mutation) => {
      if (mutation.type === 'STORE_NOTIFICATION') {
        this.addNotification(mutation.payload)
      }
    })

    // Load existing notifications
    this.loadNotifications()
  },
  methods: {
    /**
     * Loads existing notifications from the store
     */
    loadNotifications() {
      const storeNotifications = this.$store.state.notifications || []

      // Filter notifications by name if specified
      if (this.name) {
        this.notifications = storeNotifications.filter(n => n.from === this.name)
      } else {
        // Filter out authentication success notifications, but keep error/warning notifications
        this.notifications = storeNotifications.filter(n => {
          // Keep non-authentication notifications
          if (!n.from || n.from !== 'authentication') {
            return true
          }
          // For authentication notifications, only keep error and warning types
          // Suppress success notifications
          return n.type !== 'success'
        })
      }
    },

    /**
     * Adds a new notification
     * @param {Object} notification - Notification object
     */
    addNotification(notification) {
      // Filter by name if specified
      if (this.name && notification.from !== this.name) {
        return
      }

      // For authentication notifications without name filter
      if (!this.name && notification.from === 'authentication') {
        // Suppress success notifications for login operations
        // Only show error and warning notifications
        if (notification.type === 'success') {
          return
        }

        // Add unique ID if not present
        if (!notification.id) {
          notification.id = Date.now() + Math.random()
        }

        this.notifications.push(notification)

        // Auto-remove non-enhanced notifications after delay
        if (!notification.error || !notification.error.enhanced) {
          setTimeout(() => {
            this.removeNotification(notification)
          }, notification.duration || 5000)
        }
        return
      }

      // Handle regular notifications
      if (!this.name || notification.from === this.name) {
        // Add unique ID if not present
        if (!notification.id) {
          notification.id = Date.now() + Math.random()
        }

        this.notifications.push(notification)

        // Auto-remove after delay
        setTimeout(() => {
          this.removeNotification(notification)
        }, notification.duration || 5000)
      }
    },

    /**
     * Removes a notification
     * @param {Object} notification - Notification to remove
     */
    removeNotification(notification) {
      const index = this.notifications.findIndex(n => n.id === notification.id)
      if (index > -1) {
        this.notifications.splice(index, 1)
      }
    },

    /**
     * Handles account unlock event
     * @param {Object} notification - The notification that triggered unlock
     */
    handleUnlock(notification) {
      // Clear the form disabled state by removing the notification
      // The parent component will handle re-enabling the login form
      setTimeout(() => {
        this.removeNotification(notification)
      }, 3000)
    },

    /**
     * Gets CSS class for notification type
     * @param {Object} notification - Notification object
     * @returns {string} CSS class
     */
    getNotificationClass(notification) {
      const type = notification.type || 'info'

      switch (type) {
        case 'error':
          return 'is-danger'
        case 'warning':
          return 'is-warning'
        case 'success':
          return 'is-success'
        case 'info':
        default:
          return 'is-info'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.enhanced-notifications {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 1000;
  max-width: 400px;

  .auth-notifications {
    margin-bottom: 1rem;
  }

  .regular-notifications {
    .notification {
      margin-bottom: 0.5rem;
      position: relative;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .notification-content {
    padding-right: 2rem; // Space for delete button
  }

  .notification-error,
  .notification-text {
    word-wrap: break-word;
  }
}

// Responsive adjustments
@media screen and (max-width: 768px) {
  .enhanced-notifications {
    top: 0.5rem;
    right: 0.5rem;
    left: 0.5rem;
    max-width: none;
  }
}

// Dark theme support using application variables
.dashboard.is-dark {
  .enhanced-notifications {
    .notification {
      background-color: rgba(31, 33, 40, 0.9); // Using $x with opacity
      border: 1px solid rgba(221, 221, 223, 0.2); // Using $y with opacity
      color: #dddddf; // Using $y

      &.is-info {
        background-color: rgba(61, 142, 185, 0.15);
        border-color: rgba(61, 142, 185, 0.3);
        color: #3d8eb9;
      }

      &.is-success {
        background-color: rgba(72, 199, 116, 0.15);
        border-color: rgba(72, 199, 116, 0.3);
        color: #48c774;
      }

      &.is-warning {
        background-color: rgba(255, 221, 87, 0.15);
        border-color: rgba(255, 221, 87, 0.3);
        color: #ffdd57;
      }

      &.is-danger {
        background-color: rgba(255, 107, 122, 0.15);
        border-color: rgba(255, 107, 122, 0.3);
        color: #ff6b7a;
      }

      .delete {
        background-color: rgba(221, 221, 223, 0.1);

        &:hover {
          background-color: rgba(221, 221, 223, 0.2);
        }
      }
    }
  }
}
</style>
